import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { useTranslation } from 'next-i18next';
import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { ProfileMenuItem } from './header.model';
import styles from './header.module.scss';
import { cx } from '@emotion/css';
import { usePathname } from 'next/navigation';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';
import { outsideClickHandler } from '../../util/click/outside-click-handler';

const defaultImage = getDesignSystemIcon('icon-person.svg');

interface HeaderProps {
  displayName?: string;
  profileMenuItems?: ProfileMenuItem[];
  onLogout: () => void;
  className?: string;
  customHeader?: ReactNode;
  bannerHeader?: ReactNode;
}

export const Header: FC<React.PropsWithChildren<HeaderProps>> = ({
  displayName,
  profileMenuItems = [],
  onLogout,
  className,
  customHeader,
  bannerHeader,
}) => {
  const { t } = useTranslation('shared');
  const pathname = usePathname();
  const [showMenu, setShowMenu] = useState(false);
  const { tabletOnly } = useBreakpoints();
  const menuRef = useRef<HTMLDivElement>(null);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const toggleMenu = (e: any) => {
    setShowMenu(!showMenu);
    e.stopPropagation();
    outsideClickHandler(menuRef, () => setShowMenu(false));
  };

  const isActive = useCallback((requestedPath: string | null, url: string | undefined) => {
    if (!requestedPath || !url) {
      return false;
    }

    return requestedPath.replace('/[locale]', '') === url.replace(/^\/\w{2}-\w{2}/gm, '');
  }, []);

  return (
    <div
      className={cx('relative z-[31] flex flex-col justify-center bg-secondary-light p-0 lg:pr-6 print:hidden', styles.header, className)}
      data-testid="header"
    >
      {!tabletOnly && bannerHeader}
      <div className="flex flex-row-reverse items-center gap-11">
        <a href="#" onClick={toggleMenu} data-testid="profile">
          <SkeletonTheme baseColor={styles.primaryGrey}>
            <div className="flex flex-row-reverse items-center">
              {displayName ? (
                <img
                  data-testid="profileImage"
                  src={defaultImage}
                  alt="Profile badge"
                  className="mr-2 h-10 rounded-full"
                  width={32}
                  height={32}
                />
              ) : (
                <Skeleton circle={true} width={32} height={32} className="mr-2 h-10" />
              )}

              {displayName ? (
                <div className="text-md mr-2 font-stihl-contraface-text" data-testid="username">
                  {displayName}
                </div>
              ) : (
                <Skeleton width={100} className="mr-2" />
              )}
            </div>
          </SkeletonTheme>
        </a>
        {customHeader}
        {tabletOnly && bannerHeader}
      </div>
      {showMenu && (
        <div className="flex flex-row-reverse" data-testid="profileMenu" ref={menuRef}>
          <div className={cx('z-10 flex max-w-xs flex-col justify-start justify-items-start bg-primary-white shadow-md', styles.menu)}>
            {profileMenuItems.map((menuItem) => {
              return (
                <a
                  key={menuItem.id}
                  id={menuItem.id}
                  href={menuItem.url}
                  className={cx(
                    'transition-background-color bg-primary-white px-6 py-2 text-left text-secondary-deep transition hover:bg-primary-grey',
                    isActive(pathname, menuItem.url) ? 'stihl-text-body-default-bold' : 'stihl-text-body-default-reg'
                  )}
                  data-testid="profileMenuItem"
                >
                  {t(menuItem.name)}
                </a>
              );
            })}
            <a
              href="#"
              className="transition-background-color bg-primary-white px-6 py-2 text-left text-secondary-deep transition stihl-text-body-default-reg hover:bg-primary-grey"
              onClick={onLogout}
              data-testid="logout"
            >
              {t('header_menu_logout_label')}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
